// src/components/history/MagiciansTimeline.tsx
import React, { useState } from 'react';
import { HISTORICAL_MAGICIANS, HISTORICAL_ERAS, HistoricalFigure } from '../../doctrinal/historicalLineage';
import { useAudio } from '../../audio/useAudio';

export const MagiciansTimeline: React.FC = () => {
  const [activeEra, setActiveEra] = useState<string>('all');
  const [selectedFigureId, setSelectedFigureId] = useState<string | null>(null);
  const { playCardDraw, playAnvilStrike } = useAudio();

  const filteredFigures = activeEra === 'all'
    ? HISTORICAL_MAGICIANS
    : HISTORICAL_MAGICIANS.filter((f) => f.era === activeEra);

  const handleEraSelect = (eraId: string) => {
    setActiveEra(eraId);
    try {
      playCardDraw();
    } catch {
      // Audio fallback
    }
  };

  const toggleFigureExpand = (id: string) => {
    setSelectedFigureId(selectedFigureId === id ? null : id);
    try {
      playAnvilStrike();
    } catch {
      // Audio fallback
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header & Proclamation */}
      <div className="border-b border-iron-dark pb-6">
        <span className="text-xs font-display text-gold-ancient uppercase tracking-widest block">
          CHRONICON MAGORUM • LINEA CHTHONIA
        </span>
        <h2 className="font-display text-3xl sm:text-4xl text-parchment-light uppercase mt-1">
          Historical Lineage of the Unyoked
        </h2>
        <p className="text-sm text-parchment-dim mt-2 max-w-3xl font-serif italic leading-relaxed">
          Three millennia of kings, scribes, canon jurists, court astrologers, and modern adepts who invoked, 
          codified, and forged covenants with King Belial.
        </p>
      </div>

      {/* Era Navigation Tabs */}
      <div className="flex flex-wrap gap-2 pt-1" role="tablist" aria-label="Historical Eras">
        {HISTORICAL_ERAS.map((era) => {
          const isActive = activeEra === era.id;
          return (
            <button
              key={era.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => handleEraSelect(era.id)}
              className={`px-3.5 py-2 text-xs font-display tracking-wider uppercase transition-all duration-200 rounded-sm border ${
                isActive
                  ? 'border-gold-bright bg-obsidian-surface text-gold-bright shadow-glow-gold'
                  : 'border-iron-dark/80 bg-obsidian-deep/70 text-parchment-dim hover:border-iron-steel hover:text-parchment-light'
              }`}
            >
              {era.label}
            </button>
          );
        })}
      </div>

      {/* Figures Grid */}
      <div className="grid grid-cols-1 gap-6">
        {filteredFigures.map((fig: HistoricalFigure) => {
          const isExpanded = selectedFigureId === fig.id;

          return (
            <article
              key={fig.id}
              className={`chthonic-card rounded-lg p-6 sm:p-8 transition-all duration-300 border ${
                isExpanded ? 'border-gold-ancient/80 shadow-chthonic' : 'border-iron-dark/80'
              }`}
            >
              {/* Card Meta & Header */}
              <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 border-b border-iron-dark/60 pb-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-[10px] font-display uppercase tracking-widest text-gold-ancient px-2 py-0.5 rounded border border-bronze-dark/80 bg-obsidian-deep">
                      {fig.dates}
                    </span>
                    <span className="text-[10px] font-serif italic text-iron-steel">
                      {fig.tradition}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl font-bold text-parchment-light tracking-wide">
                    {fig.name}
                  </h3>
                  <div className="text-xs font-serif italic text-gold-bright mt-0.5">
                    {fig.epithet} • <span className="text-parchment-dim not-italic">{fig.role}</span>
                  </div>
                </div>

                {/* Primary Texts Tag */}
                <div className="sm:text-right mt-2 sm:mt-0">
                  <span className="text-[10px] font-display uppercase tracking-widest text-iron-steel block">
                    Canonical Manuscripts
                  </span>
                  <div className="text-xs font-serif text-parchment-light">
                    {fig.primaryTexts[0]}
                  </div>
                </div>
              </div>

              {/* Biography / Narrative Summary */}
              <div className="py-4 text-xs sm:text-sm text-parchment-dim leading-relaxed font-serif">
                <p className="whitespace-pre-line">{fig.biography}</p>
              </div>

              {/* Key Quote Callout */}
              <div className="rounded border-l-2 border-gold-bright bg-obsidian-deep/80 p-4 my-2">
                <blockquote className="font-serif italic text-xs sm:text-sm text-gold-bright leading-relaxed">
                  {fig.keyQuote}
                </blockquote>
              </div>

              {/* Expandable Gnosis & Operative Details */}
              {isExpanded && (
                <div className="mt-6 pt-6 border-t border-iron-dark/60 space-y-5 animate-fade-in">
                  {/* Complete Primary Manuscripts */}
                  <div>
                    <h4 className="font-display text-xs tracking-widest uppercase text-gold-ancient mb-2">
                      Primary Manuscripts & Inscriptions
                    </h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-parchment-light font-serif">
                      {fig.primaryTexts.map((text, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <span className="text-gold-ancient">✦</span>
                          <span>{text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Core Magical Gnosis */}
                  <div>
                    <h4 className="font-display text-xs tracking-widest uppercase text-gold-ancient mb-2">
                      Specific Gnosis Transmitted
                    </h4>
                    <ul className="space-y-2 text-xs text-parchment-dim leading-relaxed font-serif">
                      {fig.magicalGnosis.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-2.5">
                          <span className="text-ember-flare mt-0.5 text-sm leading-none">•</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Operative Magical Method */}
                  <div className="rounded border border-iron-dark bg-obsidian-surface p-4">
                    <h4 className="font-display text-[11px] tracking-widest uppercase text-gold-bright mb-1">
                      Operative Ritual Formula & Instruments
                    </h4>
                    <p className="text-xs text-parchment-dim font-serif italic">
                      {fig.operativeMethod}
                    </p>
                  </div>
                </div>
              )}

              {/* Expand / Collapse Control */}
              <div className="pt-4 flex justify-end">
                <button
                  onClick={() => toggleFigureExpand(fig.id)}
                  className="text-xs font-display tracking-widest uppercase text-gold-ancient hover:text-gold-bright transition-colors flex items-center gap-1.5 focus:outline-none"
                  aria-expanded={isExpanded}
                >
                  <span>{isExpanded ? 'Conceal Gnosis Details ▲' : 'Examine Complete Gnosis ▼'}</span>
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};
